// 主人公の致命の光 C「火花の爆ぜ」(2026-10-12・開発版 ?lunaFinisher=new の既定・決まり = src/luna_finisher.ts)。
//   ユーザー原文 (検収パック 10-12d A-2): 「Aは氷とか吹雪っぽいな。Cが良さそう」。A-4 光の強さ＝ちょうどいい (A と同じくらいに合わせる)。
//   見本 = C:/dev/LUNAIRE-notes/assets/2026-10-12-lunaire-finisher-v3/samples/look_c_spark_burst.png
//     (傷の所で白く爆ぜる芯・白金と青白の火花が抜く向きへ扇に散る・床で金の火花が跳ねる・傷のまわりに暗い煙)。
//   守る好み:
//     ・白い効果線を描かない (memory「FX に線を描かない」10-10)。見本の長い筋は「粒の頭 + 短い尾」に置き換える。尾は幅の 3.4 倍まで。
//     ・同じ丸い粒が並ぶ記号っぽさを避ける = 大きさ・寿命・生まれる時刻・重さ・色の冷め方を 1 粒ずつばらつかせる。
//     ・画面を白で塗らない (芯の光は体の幅より小さい)。赤は使わない (弾けない攻撃の専用色)。
//   色の決まり (記録 docs/research/2026-10-12-lunaire-finisher-fx-c.md §2):
//     ・青白 = ボスからほどける光 (SOUL_BLUE)。軽く、すぐ速さを失い、最後は上へ昇って消える (常夜燈の側へ・本人へは戻らない)。
//     ・金〜橙 = 刃が擦れて出る火花 (灯火の金)。重く、弧を描いて床で跳ね、冷めながら橙へ沈む。
//     ・どちらも生まれた瞬間は白 (熱い) で、冷めるにつれて色が出る。
//   描画だけ (ゲームの状態は変えない)。世界座標の ctx へ描く (render.ts の drawFx・最後の一撃は drawFinisherCFront)。
import { SOUL_BLUE } from './balance'

function h01(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453
  return x - Math.floor(x)
}
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
type RGB = [number, number, number]

// ---- 材料 (一度だけ作る小さな絵) ----
//   頭 = 芯の詰まったやわらかい点 / 尾 = 頭へ向かって濃くなる細い涙の形 / 煙 = 縁の不揃いなぼけ。
//   色の冷め方は 6 段 (白 → 色 → 沈んだ色)。
type Tex = HTMLCanvasElement
const BLUE_STAGES: RGB[] = [[244, 248, 255], [214, 230, 255], [176, 206, 250], [150, 186, 244], [124, 160, 230], [100, 130, 205]]
const GOLD_STAGES: RGB[] = [[255, 250, 228], [255, 234, 160], [252, 208, 110], [246, 172, 74], [228, 134, 52], [190, 96, 40]]
interface Mats { head: { blue: Tex[], gold: Tex[] }, tail: { blue: Tex[], gold: Tex[] }, halo: Tex, smoke: Tex[], glow: Tex, goldGlow: Tex }
let MATS: Mats | null = null

function canvas(w: number, h: number): [Tex, CanvasRenderingContext2D] {
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  return [c, c.getContext('2d')!]
}
function makeHead([r, g, b]: RGB): Tex {
  const N = 16
  const [c, x] = canvas(N, N)
  const gr = x.createRadialGradient(N / 2, N / 2, 0, N / 2, N / 2, N / 2)
  gr.addColorStop(0, 'rgba(255,255,255,1)')
  gr.addColorStop(0.18, `rgba(${r},${g},${b},1)`)
  gr.addColorStop(0.4, `rgba(${r},${g},${b},0.38)`)
  gr.addColorStop(0.7, `rgba(${r},${g},${b},0.07)`)
  gr.addColorStop(1, `rgba(${r},${g},${b},0)`)
  x.fillStyle = gr
  x.fillRect(0, 0, N, N)
  return c
}
/** 尾: 横 32 × 縦 8。右端 (x=32) が頭の側。左へ細く薄くなる涙の形 (線にしないため幅は頭と同じから始めて細る)。 */
function makeTail([r, g, b]: RGB): Tex {
  const W = 32, H = 8
  const [c, x] = canvas(W, H)
  const lg = x.createLinearGradient(0, 0, W, 0)
  lg.addColorStop(0, `rgba(${r},${g},${b},0)`)
  lg.addColorStop(0.5, `rgba(${r},${g},${b},0.3)`)
  lg.addColorStop(1, `rgba(${r},${g},${b},0.95)`)
  x.fillStyle = lg
  x.beginPath()
  x.moveTo(0, H / 2)
  x.quadraticCurveTo(W * 0.6, H / 2 - 1.2, W, 1)
  x.lineTo(W, H - 1)
  x.quadraticCurveTo(W * 0.6, H / 2 + 1.2, 0, H / 2)
  x.fill()
  return c
}
function makeSmoke(seed: number): Tex {
  const N = 64
  const [c, g] = canvas(N, N)
  for (let i = 0; i < 8; i++) {
    const a = h01(seed * 7.3 + i * 1.9) * Math.PI * 2
    const d = h01(seed * 3.1 + i * 2.7) * N * 0.2
    const cx = N / 2 + Math.cos(a) * d
    const cy = N / 2 + Math.sin(a) * d * 0.8
    const rad = N * (0.16 + h01(seed * 5.7 + i * 0.9) * 0.2)
    const al = 0.3 + h01(seed + i * 4.3) * 0.35
    const gr = g.createRadialGradient(cx, cy, 0, cx, cy, rad)
    gr.addColorStop(0, `rgba(30,32,46,${al})`)
    gr.addColorStop(0.6, `rgba(30,32,46,${al * 0.4})`)
    gr.addColorStop(1, 'rgba(30,32,46,0)')
    g.fillStyle = gr
    g.fillRect(0, 0, N, N)
  }
  g.globalCompositeOperation = 'destination-in'
  const m = g.createRadialGradient(N / 2, N / 2, N * 0.1, N / 2, N / 2, N / 2)
  m.addColorStop(0, 'rgba(0,0,0,1)')
  m.addColorStop(1, 'rgba(0,0,0,0)')
  g.fillStyle = m
  g.fillRect(0, 0, N, N)
  return c
}
function makeGlow([r, g, b]: RGB, core: number): Tex {
  const N = 64
  const [c, x] = canvas(N, N)
  const gr = x.createRadialGradient(N / 2, N / 2, 0, N / 2, N / 2, N / 2)
  gr.addColorStop(0, `rgba(255,255,255,${core})`)
  gr.addColorStop(0.2, `rgba(${r},${g},${b},0.75)`)
  gr.addColorStop(0.5, `rgba(${r},${g},${b},0.2)`)
  gr.addColorStop(1, `rgba(${r},${g},${b},0)`)
  x.fillStyle = gr
  x.fillRect(0, 0, N, N)
  return c
}
function mats(): Mats | null {
  if (typeof document === 'undefined') return null
  if (!MATS) {
    MATS = {
      head: { blue: BLUE_STAGES.map(makeHead), gold: GOLD_STAGES.map(makeHead) },
      tail: { blue: BLUE_STAGES.map(makeTail), gold: GOLD_STAGES.map(makeTail) },
      halo: makeGlow([196, 218, 252], 0.9),
      smoke: [1, 2, 3, 4, 5, 6].map(makeSmoke),
      glow: makeGlow([168, 198, 248], 0.6),
      goldGlow: makeGlow([250, 196, 110], 0.4),
    }
  }
  return MATS
}

/** 中心に置く (半径 r・縦横の伸び・向き)。 */
function putC(ctx: CanvasRenderingContext2D, base: DOMMatrix, t: Tex, x: number, y: number, r: number, stretch: number, ang: number, a: number): void {
  if (a <= 0.004 || r <= 0.15) return
  ctx.globalAlpha = Math.min(1, a)
  ctx.setTransform(base.translate(x, y).rotate(ang).scale((r * stretch) / (t.width / 2), r / (t.height / 2)))
  ctx.drawImage(t, -t.width / 2, -t.height / 2)
}
/** 火花 1 粒 = 頭 (丸) + 速い時だけ後ろへ短い尾。尾の長さ = 幅 × (stretch - 1)・stretch は 3.4 まで。 */
function spark(ctx: CanvasRenderingContext2D, base: DOMMatrix, head: Tex, tail: Tex, x: number, y: number, size: number, ang: number, stretch: number, a: number): void {
  if (a <= 0.004 || size <= 0.15) return
  if (stretch > 1.25) {
    const len = 2.4 * size * (stretch - 1)
    ctx.globalAlpha = Math.min(1, a)
    ctx.setTransform(base.translate(x, y).rotate(ang).scale(len / tail.width, (size * 1.1) / (tail.height / 2)))
    ctx.drawImage(tail, -tail.width, -tail.height / 2)
  }
  putC(ctx, base, head, x, y, size * 1.7, 1, 0, a)
}

// ---- 強さの段 ----
export interface SparkLevel {
  /** 青白 (ほどける光) と 金 (刃の火花) の数 */
  blue: number, gold: number
  /** 初速 (px/s) の幅・粒の大きさの倍率 */
  speed: [number, number], size: number
  /** 吹き出す向き (水平から上へ °・主人公の側が基準) と広がり (±°) */
  aimDeg: number, spreadDeg: number
  /** 傷の芯の光 (px・濃さ・秒) */
  flareR: number, flareA: number, flareSec: number
  /** 暗い煙のかけらの数・濃さ */
  smoke: number, smokeA: number
  /** 金の火花の寿命の倍率 (最後の一撃は床で長く残す) */
  goldLife: number
  /** 抜いた後に上へ昇る青白の残り火の数 */
  remnant: number
  /** 火花の扇のにじみ (爆ぜた直後に扇の中が明るく霞む・見本 C の火花のまわりの光)。半径 px・濃さ・秒 */
  hazeR: number, hazeA: number, hazeSec: number
}
export const SPARK_C: Record<'stab' | 'push' | 'pull' | 'finStab' | 'finPull', SparkLevel> = {
  // 刺す: 刃が入った所で小さく爆ぜる。
  stab: { blue: 18, gold: 20, speed: [140, 720], size: 1, aimDeg: 20, spreadDeg: 30, flareR: 16, flareA: 0.85, flareSec: 0.12, smoke: 3, smokeA: 0.22, goldLife: 1, remnant: 0, hazeR: 26, hazeA: 0.22, hazeSec: 0.25 },
  // 押し込む: 押した反動の小さな爆ぜ (このあと抜くまで刃が擦れて火花が出続ける = drawGrindC)。
  push: { blue: 14, gold: 22, speed: [140, 640], size: 1, aimDeg: 38, spreadDeg: 34, flareR: 18, flareA: 0.85, flareSec: 0.14, smoke: 3, smokeA: 0.2, goldLife: 1, remnant: 0, hazeR: 30, hazeA: 0.22, hazeSec: 0.28 },
  // 抜く: 一気に抜けた所から、抜く向き (主人公の側・やや上) へ大きく爆ぜる。
  pull: { blue: 130, gold: 118, speed: [180, 1400], size: 1.25, aimDeg: 24, spreadDeg: 30, flareR: 40, flareA: 1, flareSec: 0.24, smoke: 8, smokeA: 0.26, goldLife: 1.25, remnant: 18, hazeR: 86, hazeA: 0.55, hazeSec: 0.7 },
  // 最後の一撃の刺す (止めの間は、ここが爆ぜかけた姿のままゆっくり進む)。
  finStab: { blue: 56, gold: 56, speed: [150, 1000], size: 1.15, aimDeg: 22, spreadDeg: 34, flareR: 28, flareA: 0.95, flareSec: 0.22, smoke: 6, smokeA: 0.24, goldLife: 1.3, remnant: 0, hazeR: 50, hazeA: 0.3, hazeSec: 0.4 },
  // 最後の一撃の抜く: 普段の抜くと同じ作りを大きく強く (速さ 1.3 倍・芯 1.5 倍・金は床で長く残る)。
  //   10-13 ユーザー「強すぎる」(検収 10-12e B-2) で数を約 7 割へ (青 220→150・金 200→140)・閃き 58→46 / 1→0.75・霞 0.5→0.35。
  finPull: { blue: 150, gold: 140, speed: [220, 1800], size: 1.45, aimDeg: 28, spreadDeg: 34, flareR: 46, flareA: 0.75, flareSec: 0.32, smoke: 12, smokeA: 0.3, goldLife: 1.6, remnant: 0, hazeR: 120, hazeA: 0.35, hazeSec: 0.8 },
}

/** 寿命 (s)。一番長い金の火花が床で消えるまで (game.ts の ageFx)。 */
export const EXEC_C_FX_LIFE_SEC = 1.5
const GRAV_GOLD = 950
const BOUNCE = 0.32

/** 線形の空気抵抗 k と重力 g の下の位置と速さ (y は下が +)。 */
function ballistic(v0x: number, v0y: number, k: number, g: number, t: number): { dx: number, dy: number, vx: number, vy: number } {
  const e = Math.exp(-k * t)
  const vt = g / k
  return {
    dx: (v0x / k) * (1 - e),
    dy: vt * t + ((v0y - vt) / k) * (1 - e),
    vx: v0x * e,
    vy: vt + (v0y - vt) * e,
  }
}
/** 床に着く時刻 (二分法)。届かなければ null。 */
function landTime(y0: number, v0y: number, k: number, g: number, floorY: number, tMax: number): number | null {
  if (y0 + ballistic(0, v0y, k, g, tMax).dy < floorY) return null
  let lo = 0, hi = tMax
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) / 2
    if (y0 + ballistic(0, v0y, k, g, mid).dy < floorY) lo = mid; else hi = mid
  }
  return hi
}

/**
 * 1 回の爆ぜ。x,y = 傷・away = 主人公の側 (+1 右)・t = 爆ぜてからの秒・floorY = 床・seed で形を変える。
 *   slow = 止めの間の「ほとんど止まった爆ぜ」の時に 1 (尾を伸ばさず、ちらつきを止める)。
 */
export function drawSparkBurstC(ctx: CanvasRenderingContext2D, x: number, y: number, away: 1 | -1, t: number, L: SparkLevel, seed: number, floorY: number): void {
  const M = mats()
  if (!M || t < 0) return
  const base = ctx.getTransform()
  ctx.save()
  const aim = (L.aimDeg * Math.PI) / 180
  const spread = (L.spreadDeg * Math.PI) / 180
  const dirOf = (k: number, bias: number) => {
    const u = (h01(seed + k * 1.31) + h01(seed + k * 2.17) + h01(seed + k * 3.07)) / 3 - 0.5
    return aim + bias + u * 2 * spread * 1.6
  }

  // ⓪ 暗い煙 (傷のまわりにふわっと湧いて、上へ流れて薄れる・見本 C の傷の煙)。火花より先に普通の重ねで描く。
  ctx.globalCompositeOperation = 'source-over'
  for (let i = 0; i < L.smoke; i++) {
    const k = seed * 0.53 + i * 6.71
    const born = h01(k) * 0.05
    const tt = t - born
    if (tt <= 0) continue
    const life = 0.5 + h01(k + 1) * 0.45
    const lt = tt / life
    if (lt >= 1) continue
    const ang = dirOf(i + 500, 0.3)
    const dist = (30 + h01(k + 2) * 50) * (1 - Math.exp(-tt * 5))
    const px = x + away * Math.cos(ang) * dist * 0.7 + (h01(k + 3) - 0.5) * 16
    const py = y - Math.sin(ang) * dist * 0.5 - 26 * tt
    const r = (10 + h01(k + 4) * 12) * (0.5 + 0.8 * (1 - Math.exp(-tt * 4))) * L.size
    putC(ctx, base, M.smoke[(i + seed) % 6], px, py, r, 1.2, h01(k + 5) * 6, L.smokeA * clamp01(tt / 0.03) * (1 - lt) ** 1.4)
  }
  ctx.setTransform(base)

  ctx.globalCompositeOperation = 'lighter'
  // ① 芯の光 (爆ぜた瞬間だけ・すぐ引く)。半径は体の幅より小さい。
  const fk = clamp01(1 - t / L.flareSec)
  if (fk > 0) {
    putC(ctx, base, M.halo, x, y, L.flareR * (0.85 + 0.5 * (1 - fk)), 1.25, away > 0 ? 0 : Math.PI, L.flareA * fk * fk * 0.85)
    putC(ctx, base, M.head.blue[0], x, y, L.flareR * 0.42, 1, 0, L.flareA * fk)
  }

  // ①' 扇のにじみ: 爆ぜた直後、火花の扇の中が明るく霞んで引く (青白の大きな霞 + 少し下に金の小さな霞)。輪や線は描かない。
  const hk = clamp01(t / 0.03) * clamp01(1 - t / L.hazeSec)
  if (hk > 0) {
    const ox = away * Math.cos(aim) * L.hazeR * (0.55 + 0.4 * (1 - hk))
    const oy = -Math.sin(aim) * L.hazeR * 0.5
    putC(ctx, base, M.glow, x + ox, y + oy, L.hazeR * (0.8 + 0.4 * (1 - hk)), 1.6, away > 0 ? -aim : Math.PI + aim, L.hazeA * hk * hk)
    putC(ctx, base, M.goldGlow, x + ox * 0.7, y + L.hazeR * 0.12, L.hazeR * 0.6, 1.5, away > 0 ? -aim * 0.5 : Math.PI + aim * 0.5, L.hazeA * 0.7 * hk * hk)
  }

  // ② 火花 (青白と金)。生まれた瞬間は白く、冷めるにつれて色が出る。
  const n = L.blue + L.gold
  for (let i = 0; i < n; i++) {
    const gold = i >= L.blue
    const k = seed * 0.71 + i * 3.91
    const born = (h01(k + 0.1) ** 2.4) * 0.06
    const tt = t - born
    if (tt <= 0) continue
    const life = gold ? (0.32 + (h01(k + 0.5) ** 0.9) * 0.6) * L.goldLife : 0.22 + (h01(k + 0.5) ** 0.8) * 0.5
    const lt = tt / life
    if (lt >= 1) continue
    const ang = dirOf(i, gold ? -0.12 : 0.08) + (h01(k + 0.8) - 0.5) * spread * 0.5
    const sp = L.speed[0] + (h01(k + 1.1) ** 1.7) * (L.speed[1] - L.speed[0]) * (gold ? 0.85 : 1)
    const v0x = away * Math.cos(ang) * sp
    const v0y = -Math.sin(ang) * sp
    let px: number, py: number, vx: number, vy: number
    let bounced = false
    if (gold) {
      const kd = 1.5 + h01(k + 1.9) * 1.2
      const tl = landTime(y, v0y, kd, GRAV_GOLD, floorY, life)
      if (tl === null || tt < tl) {
        const b = ballistic(v0x, v0y, kd, GRAV_GOLD, tt)
        px = x + b.dx; py = y + b.dy; vx = b.vx; vy = b.vy
      } else {
        // 床で 1 回跳ねて、あとは床を滑って冷める
        const b = ballistic(v0x, v0y, kd, GRAV_GOLD, tl)
        const bx = x + b.dx
        const ux = b.vx * (0.5 + h01(k + 2.3) * 0.2)
        const uy = -Math.abs(b.vy) * BOUNCE * (0.6 + h01(k + 2.7) * 0.8)
        const t2 = tt - tl
        const tl2 = landTime(floorY - 0.01, uy, kd * 1.6, GRAV_GOLD, floorY, life)
        if (tl2 === null || t2 < tl2) {
          const c = ballistic(ux, uy, kd * 1.6, GRAV_GOLD, t2)
          px = bx + c.dx; py = floorY - 0.01 + c.dy; vx = c.vx; vy = c.vy
        } else {
          const c = ballistic(ux, 0, kd * 3, 0, t2)
          px = bx + c.dx; py = floorY - 1; vx = c.vx; vy = 0
        }
        bounced = true
      }
    } else {
      // 青白: 軽い = すぐ速さを失い、終わりは上へ昇る (ほどける光は上へ)
      const kd = 2.0 + h01(k + 1.9) * 1.8
      const g = 140 - 260 * h01(k + 2.6)
      const b = ballistic(v0x, v0y, kd, g, tt)
      px = x + b.dx; py = y + b.dy - 22 * tt * tt; vx = b.vx; vy = b.vy - 44 * tt
    }
    const v = Math.hypot(vx, vy)
    const stage = Math.min(5, Math.floor((lt ** 0.75) * 6 + (gold ? 1 : 0) + (gold && bounced ? 1 : 0)))
    const big = h01(k + 3.4)
    const size = (0.5 + big ** 2.6 * 1.4) * L.size * (gold ? 1.15 : 1) * (1 - 0.45 * lt)
    const stretch = Math.min(3.4, 1 + v / 170)
    // ちらつき: 金は冷める終わりほど強く瞬く・青白は穏やか
    const fl = gold ? 0.6 + 0.4 * Math.sin(tt * (34 + h01(k) * 30) + i) * (0.3 + lt) : 0.82 + 0.18 * Math.sin(tt * (20 + h01(k) * 14) + i)
    const a = (1 - lt) ** (gold ? 0.9 : 1.3) * clamp01(fl)
    const fam = gold ? 'gold' : 'blue'
    spark(ctx, base, M.head[fam][stage], M.tail[fam][stage], px, py, size, Math.atan2(vy, vx), stretch, a)
    // 大きい火花だけ、生まれた直後に小さなにじみ (シャボン玉に見えないよう短く・小さく)
    if (big > 0.9 && tt < 0.12) putC(ctx, base, M.glow, px, py, size * 6, 1, 0, 0.35 * (1 - tt / 0.12))
  }

  // ③ 床の照り返し (爆ぜた直後だけ・金と青白が混ざった薄い光)
  const gk = clamp01(1 - t / 0.4) * clamp01(t / 0.03)
  if (gk > 0 && L.flareR >= 20) putC(ctx, base, M.glow, x + away * 24, floorY - 2, L.flareR * 2.4, 3.2, 0, 0.16 * gk)

  // ④ 抜いた後の残り火 (青白の小さな光が上へ昇って消える・本人へは戻らない)
  for (let i = 0; i < L.remnant; i++) {
    const k = seed * 2.3 + i * 4.7
    const born = 0.18 + h01(k) * 0.45
    const tt = t - born
    if (tt <= 0) continue
    const life = 0.5 + h01(k + 1) * 0.55
    const lt = tt / life
    if (lt >= 1) continue
    const px = x + away * (8 + h01(k + 2) * 70) + Math.sin(tt * 4 + i) * 5
    const py = y - 6 - h01(k + 3) * 30 - 60 * tt - 30 * tt * tt
    putC(ctx, base, M.head.blue[2], px, py, (0.9 + h01(k + 5) * 0.9) * 2.2, 1, 0, Math.sin(lt * Math.PI) * 0.7)
  }
  ctx.setTransform(base)
  ctx.restore()
}

/**
 * 押し込んでから抜くまで、刃が傷の中で擦れて金の火花が出続ける (こじる手元が返る 0.07・0.14 秒に強く)。t = 押し込んでからの秒。
 *   抜く (押し込みから 0.25 秒) で止まる (抜いた爆ぜへ替わる)。傷の芯は熱が溜まるように少しずつ明るくなる。
 */
const GRIND_END_SEC = 0.25
export function drawGrindC(ctx: CanvasRenderingContext2D, x: number, y: number, away: 1 | -1, t: number, seed: number, floorY: number): void {
  const M = mats()
  if (!M || t < 0) return
  const base = ctx.getTransform()
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (t <= GRIND_END_SEC + 0.02) {
    const heat = clamp01(t / GRIND_END_SEC)
    const end = clamp01((GRIND_END_SEC + 0.02 - t) / 0.03)
    const pulse = 0.75 + 0.25 * Math.sin(t * 40)
    putC(ctx, base, M.glow, x, y, 7 + 7 * heat, 1.2, 0, (0.25 + 0.3 * heat) * pulse * end)
    putC(ctx, base, M.head.gold[0], x, y, 3 + 2 * heat, 1, 0, (0.5 + 0.4 * heat) * end)
  }
  // 擦れの火花: 生まれる時刻を 0〜0.25 秒に散らし、こじりの返し (0.07・0.14) に寄せる
  const N = 58
  for (let i = 0; i < N; i++) {
    const k = seed * 1.7 + i * 5.3
    const r = h01(k)
    const born = r < 0.3 ? 0.07 + (h01(k + 1) - 0.5) * 0.03 : r < 0.6 ? 0.14 + (h01(k + 1) - 0.5) * 0.03 : h01(k + 1) * GRIND_END_SEC
    const tt = t - born
    if (tt <= 0) continue
    const life = 0.14 + h01(k + 2) * 0.26
    const lt = tt / life
    if (lt >= 1) continue
    // 下と手前へ (刃の腹に沿って擦れ落ちる)
    const ang = -0.5 + h01(k + 3) * 1.4
    const sp = 140 + h01(k + 4) ** 1.5 * 420
    const v0x = away * Math.cos(ang) * sp
    const v0y = -Math.sin(ang) * sp
    const b = ballistic(v0x, v0y, 2.2, GRAV_GOLD, tt)
    const py = Math.min(floorY - 1, y + b.dy)
    const stage = Math.min(5, Math.floor(lt * 6))
    const size = (0.6 + h01(k + 5) ** 2 * 1.1) * (1 - 0.4 * lt)
    const a = (1 - lt) * (0.7 + 0.3 * Math.sin(tt * 50 + i))
    spark(ctx, base, M.head.gold[stage], M.tail.gold[stage], x + b.dx, py, size, Math.atan2(b.vy, b.vx), Math.min(3.4, 1 + Math.hypot(b.vx, b.vy) / 300), a)
  }
  ctx.setTransform(base)
  ctx.restore()
}

/** 普段の致命の各発の光 (C)。f = luna_exec の FX・age = その発からの秒 (止めの間は進まない)・floorY = 床。 */
export function drawLunaExecHitC(ctx: CanvasRenderingContext2D, f: { hit: 1 | 2 | 3, x: number, y: number, dir: 1 | -1 }, age: number, floorY: number): void {
  const away = (-f.dir) as 1 | -1
  // 止めの間 (age 0) も爆ぜ始めの姿で見せる (生まれた瞬間の点の重なりにしない)
  const t = age + 0.03
  const seed = f.hit * 17 + (Math.round(f.x) % 13)
  if (f.hit === 2) drawGrindC(ctx, f.x, f.y, away, age, seed, floorY)
  drawSparkBurstC(ctx, f.x, f.y, away, t, f.hit === 1 ? SPARK_C.stab : f.hit === 2 ? SPARK_C.push : SPARK_C.pull, seed, floorY)
}

// ============== 最後の一撃 (C) ==============
//   出どころは刺し傷 1 か所だけ (魔法陣・ボスの中心からの灰・画面いっぱいの霧と光の満ち・白い閃光は出さない)。
//   流れ (gm = 止めが明けてからのゲームの ms・src/luna_finisher.ts FIN_C):
//     止め 420ms … 刺した所で爆ぜかけた火花がほとんど止まったまま、ゆっくり広がる (止めて見せる)。傷の芯が息づく。
//     抜く (gm 50) … 大きな爆ぜ (finPull) + 光の柱が傷から天井へ立つ + 柱の中を青白い火花が昇る。
//     静けさ (〜gm 2300) … 柱が細って消え、青白の残り火が傷のあった所から少しずつ昇って消える。床の金の火花が冷めて消える。
//     名前 (gm 2300〜) … 背景の暗さが少し残ったまま、名前の文字だけが出る。
export interface FinisherCLook {
  /** 光の柱: 立ち上がり / 保つ / 消える (gm)・芯の半幅 (px)・明るさ */
  pillarRiseMs: number, pillarHoldMs: number, pillarFadeMs: number, pillarHalfWidth: number, pillarAlpha: number
  /** 柱の中を昇る火花の数 */
  pillarSparks: number
  /** 静けさの間に昇る残り火の数・出る間 (gm) */
  remnant: number, remnantFromMs: number, remnantToMs: number
}
export const FIN_C_LOOK: FinisherCLook = {
  // 10-13 ユーザー「強すぎる」(検収 10-12e B-2) で 約 7 割へ: 半幅 22→17・明るさ 0.92→0.68・昇る火花 46→32 (前の版 10-12d は 15・0.62)。
  pillarRiseMs: 200, pillarHoldMs: 650, pillarFadeMs: 1200, pillarHalfWidth: 17, pillarAlpha: 0.68,
  pillarSparks: 32,
  remnant: 34, remnantFromMs: 450, remnantToMs: 2050,
}

function pillarLevel(gmSincePull: number, L: FinisherCLook): { level: number, rise: number } {
  const t = gmSincePull
  if (t < 0) return { level: 0, rise: 0 }
  const rise = Math.min(1, t / L.pillarRiseMs)
  const afterHold = t - L.pillarRiseMs - L.pillarHoldMs
  const fade = afterHold <= 0 ? 1 : Math.max(0, 1 - afterHold / L.pillarFadeMs)
  return { level: Math.min(1, t / 50) * fade * fade, rise: 1 - (1 - rise) ** 3 }
}
export function finisherCPillarEndGm(pullAtGm: number, L: FinisherCLook = FIN_C_LOOK): number {
  return pullAtGm + L.pillarRiseMs + L.pillarHoldMs + L.pillarFadeMs
}

/**
 * 最後の一撃 (C) の前面: 刺し傷の芯 → 爆ぜ → 光の柱 → 残り火。キャラの前・HUD の下に描く。
 *   gm = 止めが明けてからのゲームの ms (止めの間は 0)・freezeP = 止めの進み (0〜1・止めの外は null)・pullAtGm = 抜く時刻。
 */
export function drawFinisherCFront(ctx: CanvasRenderingContext2D, gm: number, freezeP: number | null, pullAtGm: number,
  wound: { x: number, y: number, dir: 1 | -1 }, floorY: number, L: FinisherCLook = FIN_C_LOOK): void {
  const M = mats()
  if (!M) return
  const away = (-wound.dir) as 1 | -1
  const seed = 29
  // 刺す: 止めの間は 0.02 → 0.07 秒の姿をゆっくり (止まりかけの爆ぜ)、明けたら続きから普通に進む。
  const tStab = freezeP !== null ? 0.02 + freezeP * 0.05 : 0.07 + gm / 1000
  drawSparkBurstC(ctx, wound.x, wound.y, away, tStab, SPARK_C.finStab, seed, floorY)
  const base = ctx.getTransform()
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 傷の芯: 止めの間は息づき (形を隠さない)、抜いた後は短く消える。
  {
    let a = 0, r = 6
    if (freezeP !== null) { a = 0.7 + 0.15 * Math.sin(freezeP * Math.PI * 3); r = 5 + 2 * freezeP }
    else { const t = gm - pullAtGm; a = t < 0 ? 0.7 : Math.max(0, 0.8 * (1 - t / 260)); r = t < 0 ? 7 : 7 + t * 0.03 }
    putC(ctx, base, M.glow, wound.x, wound.y, r * 2.8, 1, 0, a * 0.5)
    putC(ctx, base, M.head.blue[0], wound.x, wound.y, r, 1, 0, a)
  }
  // 光の柱 (傷から天井へ・釣鐘の形にぼかした面。中心に細い白を作らない = 線にしない)。外側にもう 1 枚広いにじみ。
  const tp = gm - pullAtGm
  const pil = pillarLevel(tp, L)
  if (pil.level > 0.005) {
    const top = -20
    const height = (wound.y - top) * pil.rise
    const w = L.pillarHalfWidth * (0.7 + 0.3 * pil.rise)
    const SEG = Math.max(1, Math.min(90, Math.round(height / 4)))
    for (const pass of [0, 1]) {
      const ww0 = pass === 0 ? w * 2.6 : w
      const aMul = pass === 0 ? 0.32 : 1
      for (let s = 0; s < SEG; s++) {
        const y0 = Math.round(wound.y - (height * s) / SEG)
        const y1 = Math.round(wound.y - (height * (s + 1)) / SEG)
        if (y0 <= y1) continue
        const u = (s + 0.5) / SEG
        // 先端は最後の 3 割で溶かす (立ち上がりの間に柱の頭が横一文字に切れて見えた・10-12d 撮影)
        const along = (1 - u) ** 0.55 * (u < 0.05 ? u / 0.05 : 1) * Math.min(1, (1 - u) / 0.3) ** 1.5
        const ww = ww0 * (1.3 - 0.5 * u)
        const g = ctx.createLinearGradient(wound.x - ww * 3, 0, wound.x + ww * 3, 0)
        g.addColorStop(0, 'rgba(198,216,244,0)')
        g.addColorStop(0.15, 'rgba(198,216,244,0.08)')
        g.addColorStop(0.35, 'rgba(212,228,250,0.48)')
        g.addColorStop(0.5, 'rgba(236,243,255,0.85)')
        g.addColorStop(0.65, 'rgba(212,228,250,0.48)')
        g.addColorStop(0.85, 'rgba(198,216,244,0.08)')
        g.addColorStop(1, 'rgba(198,216,244,0)')
        ctx.setTransform(base)
        ctx.globalAlpha = pil.level * L.pillarAlpha * along * aMul
        ctx.fillStyle = g
        ctx.fillRect(wound.x - ww * 3, y1, ww * 6, y0 - y1)
      }
    }
    // 根元のにじみ (傷の所が一番明るい)
    putC(ctx, base, M.halo, wound.x, wound.y - 6, 30 + 20 * pil.rise, 0.8, 0, pil.level * 0.55)
    // 床の照り返し
    putC(ctx, base, M.glow, wound.x, floorY - 2, 70 * pil.rise + 20, 3.4, 0, pil.level * 0.3)
    // 柱の中を昇る青白の火花 (速い・尾は短い)
    for (let i = 0; i < L.pillarSparks; i++) {
      const k = i * 3.7 + 5
      const born = (h01(k) ** 1.4) * 0.9
      const lt = tp / 1000 - born
      if (lt <= 0) continue
      const life = 0.35 + h01(k + 1) * 0.5
      if (lt >= life) continue
      const sp = 420 + h01(k + 2) * 780
      const e = Math.exp(-1.6 * lt)
      const py = wound.y - (sp / 1.6) * (1 - e)
      if (py < top) continue
      const px = wound.x + (h01(k + 3) - 0.5) * w * 2.2 + Math.sin(lt * 9 + i) * 2
      const stage = Math.min(5, Math.floor((lt / life) * 4))
      spark(ctx, base, M.head.blue[stage], M.tail.blue[stage], px, py, 0.8 + h01(k + 4) ** 2 * 1.3, -Math.PI / 2, Math.min(3.4, 1 + (sp * e) / 300), pil.level * (1 - lt / life) * 0.95)
    }
  }
  // 静けさの残り火: 傷のあった所から青白の小さな光が少しずつ昇って消える (gm remnantFrom〜remnantTo に生まれる)。
  for (let i = 0; i < L.remnant; i++) {
    const k = i * 5.9 + 11
    const born = L.remnantFromMs + (h01(k) ** 1.3) * (L.remnantToMs - L.remnantFromMs)
    const tt = (gm - born) / 1000
    if (tt <= 0) continue
    const life = 1.0 + h01(k + 1) * 0.7
    const lt = tt / life
    if (lt >= 1) continue
    const px = wound.x + (h01(k + 2) - 0.5) * 46 + Math.sin(tt * 2.2 + i) * 7
    const py = wound.y + 10 - h01(k + 3) * 40 - (38 + h01(k + 4) * 50) * tt
    putC(ctx, base, M.head.blue[1 + (i % 3)], px, py, (0.8 + h01(k + 5) * 1.1) * 2.2, 1, 0, Math.sin(lt * Math.PI) * 0.65)
  }
  ctx.setTransform(base)
  ctx.restore()
  // 抜いた瞬間の大きな爆ぜ (柱より手前)
  if (tp >= 0) drawSparkBurstC(ctx, wound.x, wound.y, away, tp / 1000 + 0.02, SPARK_C.finPull, seed + 41, floorY)
}

/** 色の出所 (テストで青白・金から外れていないかを見る)。 */
export const SPARK_C_COLORS = { blue: BLUE_STAGES, gold: GOLD_STAGES, soul: SOUL_BLUE.head } as const
